import { errorReporter } from '@/services/errorReportingService';

/**
 * Cross-Tab Authentication Synchronization
 *
 * Uses localStorage with a storage event listener to synchronize logout
 * across all browser tabs. When a user logs out in one tab, all other
 * tabs are notified and can react accordingly.
 *
 * Key insight: sessionStorage is tab-specific, so using it for logout flags
 * meant other tabs couldn't detect when logout happened. localStorage is
 * shared across all tabs of the same origin, and the 'storage' event fires
 * in OTHER tabs when a value changes (not the tab that made the change).
 *
 * Uses a timestamp-based flag to:
 * 1. Avoid stale logout states affecting new sessions
 * 2. Auto-expire after a short window (10 seconds by default)
 */

const LOGOUT_FLAG_KEY = 'notego_logout_timestamp';
const LOGOUT_EXPIRY_MS = 10000; // Flag expires after 10 seconds (increased from 5s for slower machines)

/**
 * Set the logout flag with current timestamp.
 * Call this when user initiates logout.
 * Gracefully handles localStorage unavailability (quota exceeded, private mode, etc.)
 */
export function setLogoutFlag(): void {
  try {
    localStorage.setItem(LOGOUT_FLAG_KEY, Date.now().toString());
  } catch (error) {
    // Log but don't block logout - cross-tab sync is a convenience feature
    errorReporter.expected('Failed to set cross-tab logout flag', error, {
      feature: 'auth',
      operation: 'setLogoutFlag',
    });
  }
}

/**
 * Clear the logout flag.
 * Call this when user successfully logs back in.
 * Gracefully handles localStorage unavailability.
 */
export function clearLogoutFlag(): void {
  try {
    localStorage.removeItem(LOGOUT_FLAG_KEY);
  } catch (error) {
    errorReporter.expected('Failed to clear cross-tab logout flag', error, {
      feature: 'auth',
      operation: 'clearLogoutFlag',
    });
  }
}

/**
 * Check if a logout occurred recently (within LOGOUT_EXPIRY_MS).
 * This replaces the sessionStorage check for 'userRequestedLogout'.
 *
 * @returns true if logout happened within the expiry window
 */
export function isRecentLogout(): boolean {
  try {
    const timestamp = localStorage.getItem(LOGOUT_FLAG_KEY);
    if (!timestamp) return false;

    const parsedTime = parseInt(timestamp, 10);
    if (Number.isNaN(parsedTime)) {
      // Corrupted data - clear it and return false
      clearLogoutFlag();
      return false;
    }

    const elapsed = Date.now() - parsedTime;
    return elapsed >= 0 && elapsed < LOGOUT_EXPIRY_MS;
  } catch {
    // localStorage unavailable - assume no recent logout
    return false;
  }
}

/**
 * CONTRACT #1 Part 3 — the guard auto-login paths must use.
 *
 * True when a logout is either in flight or recently completed. Checks BOTH
 * signals because each covers a gap the other doesn't:
 *
 * - `userRequestedLogout` (sessionStorage) is durable: it is set synchronously
 *   at the start of `doSignOut` and only cleared once logout actually completes.
 *   If `auth.signOut()` throws, this stays set.
 * - `isRecentLogout()` (localStorage) is cross-tab but expires after 10s.
 *
 * Checking only the timestamp meant a failed `signOut()` let the 10s window
 * lapse while the user was still, from the app's perspective, logging out —
 * extension auto-login was then permitted, `authStore` force-signed them out,
 * and the login/logout churn began.
 */
export function isLogoutInProgress(): boolean {
  try {
    if (sessionStorage.getItem('userRequestedLogout') === 'true') {
      return true;
    }
  } catch {
    // sessionStorage unavailable (private mode / sandboxed iframe) — fall through.
  }
  return isRecentLogout();
}

/**
 * Subscribe to logout events from other tabs.
 *
 * The 'storage' event only fires in OTHER tabs when localStorage changes,
 * not in the tab that made the change. This is perfect for cross-tab sync.
 *
 * @param callback - Function to call when logout is detected from another tab
 * @returns Cleanup function to remove the event listener
 */
export function subscribeToLogout(callback: () => void): () => void {
  const handler = (event: StorageEvent) => {
    // Only react to our specific logout key being set (not cleared)
    if (event.key === LOGOUT_FLAG_KEY && event.newValue) {
      callback();
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
